import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TryOurTools from '../components/TryOurTools';

const getVideoId = (input: string): string | null => {
  try {
    const url = new URL(input);
    if (url.hostname.endsWith('youtube.com')) {
      return url.searchParams.get('v');
    }
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }
  } catch {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  }
  return null;
};

const TimestampLinkGenerator: React.FC = () => {
  const [input, setInput] = useState('');
  const [start, setStart] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLink('');
    const id = getVideoId(input.trim());
    if (!id) {
      setError('Please enter a valid YouTube URL or video ID.');
      return;
    }
    if (!/^\d+$/.test(start.trim())) {
      setError('Please enter a valid start time in seconds.');
      return;
    }
    const url = `https://www.youtube.com/watch?v=${id}&t=${start}s`;
    setLink(url);
  };

  return (
    <>
      <Helmet>
        <title>YouTube Timestamp Link Generator - Free Tool</title>
        <meta name="description" content="Generate a YouTube link that starts at a specific time. Enter a YouTube URL or video ID and a start time to get your custom link instantly." />
        <meta name="keywords" content="youtube timestamp link, youtube time link, youtube start time link, youtube link generator" />
        <link rel="canonical" href="https://youtubeplayer.com/timestamp-link-generator" />
      </Helmet>
      <div className="max-w-xl mx-auto py-10">
        <h1 className="text-3xl font-bold mb-2 text-light-text dark:text-dark-text text-center">YouTube Timestamp Link Generator</h1>
        <p className="text-center text-light-subtle dark:text-dark-subtle mb-8">Create a YouTube link that starts at a specific time. 100% free, no login needed.</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 mb-6">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
            className="px-4 py-3 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
            autoFocus
          />
          <input
            type="number"
            value={start}
            onChange={e => setStart(e.target.value)}
            placeholder="Start time (seconds)"
            className="px-4 py-3 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
            min={0}
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-base hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
          >
            Generate Link
          </button>
        </form>
        {error && <div className="text-red-600 dark:text-red-400 mb-4 text-center">{error}</div>}
        {link && (
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-6 mt-6 flex flex-col items-center">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-base hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors mb-2"
            >
              Open Link
            </a>
            <div className="text-xs text-light-subtle dark:text-dark-subtle break-all">{link}</div>
          </div>
        )}
        <div className="mt-8 mb-2 p-4 rounded-lg bg-gradient-to-r from-red-100 to-purple-100 dark:from-red-900/30 dark:to-purple-900/30 border border-light-border dark:border-dark-border text-light-text dark:text-dark-text shadow">
          <strong>YouTube Timestamp Link Generator:</strong> Instantly create a <b>YouTube link with a custom start time</b>. Enter any YouTube URL or video ID and specify the start time in seconds to generate a direct link. No login or API required. Perfect for sharing specific moments in YouTube videos on social media, blogs, or websites. 100% free and SEO-friendly.
        </div>
        <TryOurTools />
      </div>
    </>
  );
};

export default TimestampLinkGenerator; 